import { useMemo } from "react";
import { AlertTriangle } from "lucide-react";
import {
  findTripDateCollisions,
  type TripDateCollision,
} from "@mtform/shared";
import { Checkbox } from "@/components/ui/checkbox";
import { formatRecurringDate } from "@/lib/recurrence";
import {
  isTerminalCancelStatus,
  prettyStatus,
  statusBadgeClasses,
} from "@/lib/trip-status";
import { cn } from "@/lib/utils";
import { usePatientLookupState } from "./PatientLookupContext";

// =============================================================================
// Same-date duplicate gate
//
// Mount Sinai, Aug 2026: the same patient was booked for the same day by two
// departments a day apart. ~a dozen departments book into one portal and none
// can see another's bookings.
//
// The lookup step's existing escalation could not catch it — it only fires for
// a trip TODAY or one booked in the last 6h, and no date has been chosen at
// that point anyway. This asks the question that matters, once a date exists:
// does this patient already have a trip that day?
//
// Rendered at TWO points on purpose:
//   * the transport-date step, where the decision is made (best UX), and
//   * the review step, which is the last thing before dispatch.
//
// The second is not belt-and-braces. Restoring an auto-saved draft jumps
// straight to the requester step with the date already marked answered, so the
// date card renders its collapsed summary and the gate never runs — precisely
// the interrupted-booking-resumed-next-day timeline this feature exists for.
// The acknowledgement is shared, so acknowledging at either point is enough.
// =============================================================================

/**
 * Collisions between the dates being booked and the patient's trips on file,
 * plus a key identifying this exact set.
 *
 * Trips already cancelled are excluded: `loadUpcomingBookings` filters on our
 * local `canceled_at`, which only catches cancels made through this app. Most
 * bookers cannot cancel in-app (the `cancelTrip` permission is admin-only), so
 * cancellations happen in TripLink and land here as a live-looking row. Making
 * a mandatory gate fire over a trip the booker themselves just cancelled is
 * exactly how a safety prompt becomes something staff tick without reading.
 */
export function useTripDateCollisions(datesBeingBooked: string[]): {
  collisions: TripDateCollision[];
  collisionKey: string;
} {
  const lookup = usePatientLookupState();

  const upcoming = useMemo(() => {
    const all =
      lookup.status === "found" ? (lookup.patient?.upcomingBookings ?? []) : [];
    return all.filter((b) => !isTerminalCancelStatus(b.status));
  }, [lookup]);

  const collisions = useMemo(
    () => findTripDateCollisions(datesBeingBooked, upcoming),
    [datesBeingBooked, upcoming],
  );

  // The key carries the TRIP IDS, not just the dates. Keying on dates alone
  // meant an acknowledgement survived a patient switch: the booker corrects a
  // mistyped MRN, the new patient happens to have a trip on the same day, and
  // the box is still ticked with Continue live — the exact "warning visible,
  // nothing stopping you" state this feature exists to remove.
  const collisionKey = useMemo(
    () =>
      collisions
        .map((c) => `${c.date}:${c.existing.map((b) => b.friendlyId).join("|")}`)
        .join(","),
    [collisions],
  );

  return { collisions, collisionKey };
}

/** How many colliding trips to list before summarising the rest. Same reason
 *  the lookup panel caps: a fully-overlapping recurring series would otherwise
 *  render dozens of rows under a single checkbox. */
const MAX_LISTED = 6;

export function DuplicateDateWarning({
  collisions,
  acknowledged,
  onAcknowledge,
}: {
  collisions: TripDateCollision[];
  acknowledged: boolean;
  onAcknowledge: (checked: boolean) => void;
}) {
  const rows = collisions.flatMap((c) =>
    c.existing.map((b) => ({ date: c.date, booking: b })),
  );
  const multiDate = collisions.length > 1;
  const title = multiDate
    ? `This patient already has trips booked on ${collisions.length} of these dates`
    : rows.length === 1
      ? "This patient already has a trip booked on this date"
      : `This patient already has ${rows.length} trips booked on this date`;

  return (
    <div
      role="alert"
      aria-live="polite"
      className="rounded-2xl border border-amber-400/45 bg-amber-400/[0.08] px-3 py-3"
    >
      <div className="flex items-start gap-2">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
        <div className="min-w-0">
          <p className="text-sm font-semibold text-foreground">{title}</p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Another department may have already arranged transport. Check before
            booking a second trip.
          </p>
        </div>
      </div>

      <ul className="mt-2 space-y-1.5 pl-6">
        {rows.slice(0, MAX_LISTED).map(({ date, booking: b }) => (
          <li
            key={`${date}-${b.friendlyId}`}
            className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-xs"
          >
            <span className="font-mono font-semibold text-foreground/90">
              {b.friendlyId}
            </span>
            {b.status ? (
              // The live status matters here: a trip already finished or on
              // the road is a different decision from one still pending.
              <span
                className={cn(
                  "rounded-full border px-1.5 py-px text-[10px] font-semibold uppercase tracking-wide",
                  statusBadgeClasses(b.status),
                )}
              >
                {prettyStatus(b.status)}
              </span>
            ) : null}
            <span className="text-foreground/80">
              {formatRecurringDate(date)}
              {formatCollisionTime(b) ? ` at ${formatCollisionTime(b)}` : ""}
            </span>
            {b.createdByName ? (
              <span className="text-muted-foreground">
                booked by {b.createdByName}
              </span>
            ) : null}
          </li>
        ))}
      </ul>
      {rows.length > MAX_LISTED ? (
        <p className="mt-1.5 pl-6 text-xs text-muted-foreground">
          + {rows.length - MAX_LISTED} more on other selected dates.
        </p>
      ) : null}

      <label className="mt-2.5 flex cursor-pointer items-start gap-2 text-xs font-medium text-foreground">
        <Checkbox
          checked={acknowledged}
          onCheckedChange={(checked) => onAcknowledge(checked === true)}
          aria-label="Confirm this is not a duplicate trip"
          className="mt-0.5"
        />
        <span>
          I&rsquo;ve checked &mdash; this is a separate trip, not a duplicate.
        </span>
      </label>
    </div>
  );
}

/** Time to show against a colliding trip: appointment-anchored trips show the
 *  appointment, will-calls say so, otherwise the estimated pickup. Blank when
 *  the trip carries no clock at all. */
function formatCollisionTime(b: TripDateCollision["existing"][number]): string {
  if (b.pickupTimeEstimated === "WILL_CALL") return "Will Call";
  return (b.appointmentTime || b.pickupTimeEstimated || "").trim();
}
